import React from 'react';
import styled from 'styled-components';
import { Table, Tag, Button, Space, Popconfirm, Tooltip, Empty } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  PiFileZipBold,
  PiTrashBold,
  PiPencilSimpleBold,
  PiDatabaseBold,
  PiCalendarBlankBold,
} from 'react-icons/pi';
import { CollectFormTemplate } from '~/common/typescript/models/CollectFormTemplate';
import { formatDate } from '~/utils/dateUtils';

interface CollectFormTemplateListProps {
  forms: CollectFormTemplate[];
  loading: boolean;
  onEdit: (form: CollectFormTemplate) => void;
  onDelete: (uuid: string) => void;
  onExportZip: (form: CollectFormTemplate) => void;
  currentPage: number;
  pageSize: number;
  totalCount: number;
  onPageChange: (page: number, pageSize: number) => void;
}

export const CollectFormTemplateList: React.FC<CollectFormTemplateListProps> = ({
  forms,
  loading,
  onEdit,
  onDelete,
  onExportZip,
  currentPage,
  pageSize,
  totalCount,
  onPageChange,
}) => {
  const columns: ColumnsType<CollectFormTemplate> = [
    {
      title: 'Tên mẫu biểu mẫu',
      dataIndex: 'name',
      key: 'name',
      render: (name: string, record) => (
        <UI_NameCell>
          <UI_NameText onClick={() => onEdit(record)}>{name}</UI_NameText>
          {record.description && (
            <UI_DescText title={record.description}>{record.description}</UI_DescText>
          )}
        </UI_NameCell>
      ),
    },
    {
      title: 'Cấu trúc',
      key: 'structure',
      width: 180,
      render: (_, record) => {
        const fieldCount = record.content?.fields?.length || 0;
        const sectionCount = record.content?.sections?.length || 0;
        return (
          <Space size={4} wrap>
            <Tag color="blue">{fieldCount} trường</Tag>
            {sectionCount > 0 && <Tag color="cyan">{sectionCount} phần</Tag>}
          </Space>
        );
      },
    },
    {
      title: 'SQLite DB',
      dataIndex: 'sqlite_files',
      key: 'sqlite_files',
      width: 140,
      render: (files: string) => {
        if (!files) return <span style={{ color: '#94a3b8' }}>-</span>;
        return (
          <Tag icon={<PiDatabaseBold style={{ verticalAlign: -1 }} />} color="purple">
            Có đính kèm
          </Tag>
        );
      },
    },
    {
      title: 'Ngày tạo',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 150,
      render: (date: string) => (
        <UI_DateText>
          <PiCalendarBlankBold size={13} />
          <span>{formatDate(date)}</span>
        </UI_DateText>
      ),
    },
    {
      title: 'Thao tác',
      key: 'actions',
      width: 170,
      align: 'right',
      render: (_, record) => (
        <Space size={6}>
          <Tooltip title="Chỉnh sửa trong Form Builder">
            <Button
              size="small"
              type="primary"
              ghost
              icon={<PiPencilSimpleBold size={14} />}
              onClick={() => onEdit(record)}
            >
              Thiết kế
            </Button>
          </Tooltip>

          <Tooltip title="Xuất gói ZIP">
            <Button
              size="small"
              icon={<PiFileZipBold size={14} />}
              onClick={() => onExportZip(record)}
            />
          </Tooltip>

          <Popconfirm
            title="Xoá biểu mẫu"
            description="Bạn có chắc chắn muốn xoá mẫu biểu mẫu này không?"
            onConfirm={() => onDelete(record.uuid)}
            okText="Xoá"
            cancelText="Huỷ"
            okButtonProps={{ danger: true }}
          >
            <Button size="small" danger type="text" icon={<PiTrashBold size={14} />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <UI_TableWrapper>
      <Table
        dataSource={forms}
        columns={columns}
        rowKey="uuid"
        loading={loading}
        size="middle"
        pagination={{
          current: currentPage,
          pageSize: pageSize,
          total: totalCount,
          pageSizeOptions: [10, 20, 50],
          showSizeChanger: true,
          showTotal: (total, range) => `${range[0]}-${range[1]} của ${total} template`,
          onChange: onPageChange,
        }}
        locale={{
          emptyText: <Empty description="Không có mẫu biểu mẫu nào" />,
        }}
      />
    </UI_TableWrapper>
  );
};

const UI_TableWrapper = styled.div`
  padding: 16px 20px;

  .ant-table-wrapper {
    background-color: #fff;
    border-radius: 8px;
    border: 1px solid #f1f5f9;
  }
`;

const UI_NameCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const UI_NameText = styled.span`
  font-weight: 500;
  color: #1e293b;
  cursor: pointer;

  &:hover {
    color: #2563eb;
    text-decoration: underline;
  }
`;

const UI_DescText = styled.span`
  font-size: 12px;
  color: #64748b;
  max-width: 320px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const UI_DateText = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: #64748b;
`;
